import React, { useState, useEffect } from 'react';
import { Lesson, User, Stats } from '../../types';
import TypingTutor from '../core/TypingTutor';
import TypingGame from '../core/TypingGame';
import WordBurstGame from '../games/WordBurstGame';
import BubblePopGame from '../games/BubblePopGame';
import CarRaceGame from '../games/CarRaceGame';
import TypingRpgGame from '../games/TypingRpgGame';
import FallingWordsGame from '../games/FallingWordsGame';
import SearchOfThe67Game from '../games/SearchOfThe67Game';
import TurkeyChaseGame from '../games/TurkeyChaseGame';
import { CATALOG_GAMES } from '../games/gameCatalog';
import GameCard from '../ui/GameCard';
import GameContainer from '../ui/GameContainer';
import { api } from '../../utils/api';
import LessonCard from '../ui/LessonCard';
import LessonSectionHeader from '../ui/LessonSectionHeader';
import LoadingSpinner from '../ui/LoadingSpinner';

const GuestDashboard: React.FC = () => {
    const [tab, setTab] = useState<'lessons' | 'games'>('lessons');
    const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
    const [selectedGame, setSelectedGame] = useState<string | null>(null);
    
    const [guestLessons, setGuestLessons] = useState<Lesson[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchGuestLessons = async () => {
            setLoading(true);
            setError(null);
            try {
                const allSystemLessons = await api.get<Lesson[]>('/api/v1/lessons/system');
                setGuestLessons(allSystemLessons.slice(0, 10));
            } catch (err) {
                if (err instanceof Error) {
                    setError("Could not load demo lessons. Please try again later.");
                }
            } finally {
                setLoading(false);
            }
        };

        if (tab === 'lessons') {
            fetchGuestLessons();
        } else {
            setLoading(false);
        }
    }, [tab]);


    const handleGuestLessonComplete = (lessonId: string | number, stats: Omit<Stats, 'errors'>) => {
        // Guest progress is not saved, so this is a no-op.
    };

    const handleGuestExit = () => {
        setSelectedLesson(null);
    };

    const renderLessons = () => {
        if (loading) return <LoadingSpinner message="Loading demo lessons..." />;
        if (error) return <div className="text-red-500 text-center bg-red-100 p-3 rounded-md">{error}</div>;

        if (selectedLesson) {
            const currentIndex = guestLessons.findIndex(l => l.lesson_id === selectedLesson.lesson_id);
            const nextLesson = (currentIndex !== -1 && currentIndex + 1 < guestLessons.length) ? guestLessons[currentIndex + 1] : null;
            const handleStartNextGuestLesson = nextLesson ? () => setSelectedLesson(nextLesson) : undefined;
            
            return (
                <TypingTutor 
                    lesson={selectedLesson} 
                    onLessonComplete={handleGuestLessonComplete} 
                    onExit={handleGuestExit}
                    onStartNextLesson={handleStartNextGuestLesson}
                />
            );
        }

        return (
            <div>
              <LessonSectionHeader title="Tutorial Lessons (1-10)" theme={1} />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {guestLessons.map((lesson, index) => (
                    <LessonCard 
                      key={lesson.lesson_id}
                      lessonNumber={index + 1}
                      title={lesson.title}
                      status={'unlocked'}
                      onClick={() => setSelectedLesson(lesson)}
                      isFirst={index === 0}
                      theme={index}
                    />
                ))}
              </div>
            </div>
        );
    };

    const renderGames = () => {
        const handleBack = () => setSelectedGame(null);
        const mockUser: User = { user_id: 'guest', email: 'guest@test.com', first_name: 'Guest', last_name: 'User', role: 'student', school_id: 'none' };
        const catalogGame = CATALOG_GAMES.find(g => g.id === selectedGame);
        if (catalogGame) {
            return <GameContainer title={catalogGame.title} icon={catalogGame.icon} subtitle={catalogGame.subtitle} onBack={handleBack}>{catalogGame.render(mockUser)}</GameContainer>;
        }

        switch (selectedGame) {
            case 'solo': return <GameContainer title="Timed Race" icon="⏱️" subtitle="Speed drill: type as many words as you can before the clock runs out." onBack={handleBack}><TypingGame user={mockUser} /></GameContainer>;
            case 'word-burst': return <GameContainer title="Word Burst" icon="💥" subtitle="Vocabulary drill: type each word fully before it fades away." onBack={handleBack}><WordBurstGame user={mockUser} /></GameContainer>;
            case 'bubble-pop': return <GameContainer title="Bubble Pop" icon="🫧" subtitle="Key recall: hit the letter shown on each bubble before it floats off." onBack={handleBack}><BubblePopGame user={mockUser} /></GameContainer>;
            case 'car-race': return <GameContainer title="Car Race" icon="🏎️" subtitle="Speed drill: type words correctly to keep your car in the lead." onBack={handleBack}><CarRaceGame /></GameContainer>;
            case 'typing-rpg': return <GameContainer title="Typing RPG: The Cave of Curses" icon="🐉" subtitle="Adventure mode: type commands to battle monsters and save the kingdom." onBack={handleBack}><TypingRpgGame user={mockUser} /></GameContainer>;
            case 'falling-words': return <GameContainer title="Falling Words" icon="🌧️" subtitle="Reaction drill: clear words before they reach the bottom." onBack={handleBack}><FallingWordsGame user={mockUser} /></GameContainer>;
            case 'search-of-the-67': return <GameContainer title="In Search of the 67" icon="🗝️" subtitle="Accuracy challenge: type each of the 67 challenges perfectly to advance." onBack={handleBack}><SearchOfThe67Game user={mockUser} /></GameContainer>;
            case 'turkey-chase': return <GameContainer title="Who Will Catch the Turkey?" icon="🦃" subtitle="Reflex drill: type fast and clean -- 3 mistakes and the turkey gets away." onBack={handleBack}><TurkeyChaseGame /></GameContainer>;
            default:
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {CATALOG_GAMES.filter(g => g.featured).map(g => (
                            <GameCard key={g.id} title={g.title} description={g.description} onClick={() => setSelectedGame(g.id)} color={g.color} icon={g.icon} skill={g.skill} />
                        ))}
                        <GameCard title="Who Will Catch the Turkey?" description="A Thanksgiving race! Type fast and don't make mistakes to catch the turkey." onClick={() => setSelectedGame('turkey-chase')} color="red" icon="🦃" skill="Reflexes" />
                        <GameCard title="Timed Race" description="Type as many words as you can in 60 seconds." onClick={() => setSelectedGame('solo')} color="blue" icon="⏱️" skill="Speed"/>
                        <GameCard title="Word Burst" description="Type the words before they disappear from the screen." onClick={() => setSelectedGame('word-burst')} color="purple" icon="💥" skill="Vocabulary"/>
                        <GameCard title="Bubble Pop" description="Pop the bubbles by typing the correct letter." onClick={() => setSelectedGame('bubble-pop')} color="red" icon="🫧" skill="Key Recall"/>
                        <GameCard title="Car Race" description="Type words correctly to speed to the finish line." onClick={() => setSelectedGame('car-race')} color="red" icon="🏎️" skill="Speed"/>
                        <GameCard title="Falling Words" description="Type the words before they hit the bottom!" onClick={() => setSelectedGame('falling-words')} color="blue" icon="🌧️" skill="Reaction Time"/>
                        <GameCard title="In Search of the 67" description="A typing adventure! One mistake and you start over." onClick={() => setSelectedGame('search-of-the-67')} color="purple" icon="🗝️" skill="Accuracy" />
                        <GameCard title="Typing RPG" description="An epic adventure! Save your village by mastering the keyboard, level by level." onClick={() => setSelectedGame('typing-rpg')} color="blue" icon="🐉" skill="Adventure"/>
                        <GameCard title="Multiplayer Race" description="Race against your classmates in real-time!" onClick={() => {}} disabled={true} disabledText="Sign up to race with friends!" color="green" icon="🏁" skill="Competition"/>
                        {CATALOG_GAMES.filter(g => !g.featured).map(g => (
                            <GameCard key={g.id} title={g.title} description={g.description} onClick={() => setSelectedGame(g.id)} color={g.color} icon={g.icon} skill={g.skill} />
                        ))}
                    </div>
                );
        }
    };

    return (
        <div className="w-full max-w-7xl">
            <div className="p-4 mb-6 bg-blue-100 border-l-4 border-blue-500 text-blue-700">
                <h3 className="font-bold">Welcome, Guest!</h3>
                <p>You can try the first 10 lessons and all games. Sign up to save your progress and unlock all features!</p>
            </div>

            <div className="flex border-b border-gray-300 mb-6">
                <button className={`py-2 px-6 font-semibold ${tab === 'lessons' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`} onClick={() => setTab('lessons')}>Lessons</button>
                <button className={`py-2 px-6 font-semibold ${tab === 'games' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`} onClick={() => setTab('games')}>Games</button>
            </div>

            {tab === 'lessons' && renderLessons()}
            {tab === 'games' && renderGames()}
        </div>
    );
};

export default GuestDashboard;
